import { useMemo } from 'react'
import { Outlet } from 'react-router-dom'

import { useTour } from '@/shared/components/DriverTour/libs/useTour'
import { useLocalStorage } from '@/shared/lib/useLocalStorage'
import { useAppSelector } from '@/shared/store'
import { styled } from '@mui/material'

import { GlobalDialogProvider } from '../providers/GlobalDialogProvider/GlobalDialogProvider'
import { GlobalLoader } from './GlobalLoader'
import { LayoutContext } from './LayoutContext'
import { LoadingScreen } from './LoadingScreen'
import { Navigation } from './Navigation'
import { ProtectedRoute } from './route/ProtectedRoute'

export type LayoutProps = {
  children?: React.ReactNode
}

export const Layout: React.FC<LayoutProps> = () => {
  const [isNavOpen, setNavOpen] = useLocalStorage('isNavOpen', true)
  const { isComplete } = useAppSelector(state => state.loader)

  useTour('layout', isComplete)

  const navContext = useMemo(() => ({
    isNavOpen,
    setNavOpen,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isNavOpen])

  return (
    <GlobalDialogProvider>
      <StyledContainer>
        <LayoutContext.Provider value={navContext}>
          <Navigation />
        </LayoutContext.Provider >
        <div style={{ width: '100%', height: '100vh' }}>
          <StyledMain $isNavOpen={isNavOpen}>
            <ProtectedRoute key={window.location.pathname}>
              <Outlet />
            </ProtectedRoute>
          </StyledMain>
        </div>
        <GlobalLoader />
        <div id="session-expired-dialog" />
        <div id="modal" />
        <LoadingScreen />
      </StyledContainer>
    </GlobalDialogProvider>
  )
}


export const StyledMain = styled('main', {
  shouldForwardProp: (prop) => prop !== '$isNavOpen',
}) <{ $isNavOpen?: boolean }>`
  position: relative;
  margin-left: 0;
  transition: margin-left ${(props) => props.theme.layout.navSidebarTransitionSpeed}ms ease-in-out,
    width ${(props) => props.theme.layout.navSidebarTransitionSpeed}ms ease-in-out,
    transform ${(props) => props.theme.layout.navSidebarTransitionSpeed}ms ease-in-out;
  height: 100%;
  
  ${(props) => props.$isNavOpen && `
    transform: translateX(${props.theme.layout.navSidebarWidth});
  `}

  ${(props) => props.theme.breakpoints.up('sm')} {
    padding: 0;
    width: calc(100% - ${(props) => props.theme.layout.navSidebarCollapsedWidth});
    transform: translateX(${(props) => props.theme.layout.navSidebarCollapsedWidth});
    height: 100%;
    
    ${(props) => props.$isNavOpen && `
      width: calc(100% - ${props.theme.layout.navSidebarWidth});
      transform: translateX(${props.theme.layout.navSidebarWidth});
    `}
  }
`

export const StyledContainer = styled('div')`
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.palette.background.default};
`
